// ============================================================================
// Formats ACCUEIL E1a — helpers locale-aware (FR+EN des l'ecriture)
// ============================================================================
// Les helpers historiques src/lib/format/* sont fr-CA FIGES (heritage PoC —
// consommes par les comites). L'accueil est bilingue natif : chaque format
// prend la locale i18n courante. Fonctions pures → testables (format.test.ts).

const localeTag = (locale: string): string => (locale.startsWith('en') ? 'en-CA' : 'fr-CA');

/** « Mardi 14 juillet 2026 » / « Tuesday, July 14, 2026 » — 1re lettre capitalisee. */
export function formatDayDate(date: Date, locale: string): string {
  const label = new Intl.DateTimeFormat(localeTag(locale), {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** Periode 'YYYY-MM' → « avril 2026 » / « April 2026 » (UTC — pas de recul de fuseau). */
export function formatMonthLong(period: string, locale: string): string {
  const [year, month] = period.split('-').map(Number);
  if (!year || !month) return period;
  return new Intl.DateTimeFormat(localeTag(locale), {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, 1)));
}

/** Montant CAD entier (« 2 345 782 $ » / « $2,345,782 »). */
export function formatCad(value: number, locale: string): string {
  return new Intl.NumberFormat(localeTag(locale), {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Montant CAD compact pour les cellules stat etroites — locale-aware via Intl
 * (`notation: 'compact'`). Sous 1 000 $, montant entier plein (`formatCad`).
 */
export function formatCompactCad(value: number, locale: string): string {
  if (Math.abs(value) < 1_000) return formatCad(value, locale);
  return new Intl.NumberFormat(localeTag(locale), {
    notation: 'compact',
    compactDisplay: 'short',
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 1,
  }).format(value);
}

/** Ratio (0.128) → « 12,8 % » SANS signe — le sens est porte par ▲/▼ du delta. */
export function formatDeltaPct(ratio: number, locale: string): string {
  if (!Number.isFinite(ratio)) return '—';
  return new Intl.NumberFormat(localeTag(locale), {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(Math.abs(ratio));
}

/** Score humeur 1..5 → « 4.3 » (1 decimale, separateur locale). */
export function formatMood(score: number, locale: string): string {
  return new Intl.NumberFormat(localeTag(locale), {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(score);
}

/** Temps relatif locale-aware (« il y a 14 jours » / « 14 days ago »). */
export function formatRelative(input: string | Date, locale: string, now: Date = new Date()): string {
  const d = typeof input === 'string' ? new Date(input) : input;
  if (Number.isNaN(d.getTime())) return '—';
  const rtf = new Intl.RelativeTimeFormat(localeTag(locale), { numeric: 'auto' });
  const diffMin = Math.round((now.getTime() - d.getTime()) / 60_000);
  if (Math.abs(diffMin) < 60) return rtf.format(-diffMin, 'minute');
  const diffH = Math.round(diffMin / 60);
  if (Math.abs(diffH) < 24) return rtf.format(-diffH, 'hour');
  const diffD = Math.round(diffH / 24);
  if (Math.abs(diffD) < 30) return rtf.format(-diffD, 'day');
  const diffMonth = Math.round(diffD / 30);
  if (Math.abs(diffMonth) < 12) return rtf.format(-diffMonth, 'month');
  return rtf.format(-Math.round(diffMonth / 12), 'year');
}
